import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../../lib/api';
import logoImage from '../../assets/920a151e35875b2a6468c8afe0418ba4ce7adc2e.png';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
            ux_mode?: string;
          }) => void;
          renderButton: (
            element: HTMLElement,
            config: {
              theme?: string;
              size?: string;
              text?: string;
              shape?: string;
              width?: number;
            },
          ) => void;
        };
      };
    };
  }
}

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

export function LoginPage() {
  const navigate = useNavigate();
  const buttonRef = useRef<HTMLDivElement>(null);
  // Phase 25-03: derive config-error from build-time constant directly.
  // GOOGLE_CLIENT_ID is `import.meta.env.VITE_GOOGLE_CLIENT_ID` (line ~33);
  // its value is fixed for the lifetime of the page. Initial state reads
  // the constant; no useEffect+setError needed for the config-missing branch.
  const [error, setError] = useState<string | null>(
    GOOGLE_CLIENT_ID ? null : 'Google Client ID not configured (VITE_GOOGLE_CLIENT_ID)',
  );
  const [loading, setLoading] = useState(false);

  // In dev mode with DEV_AUTH_BYPASS, skip the login page entirely
  const isDev = import.meta.env.DEV;
  const devUser = import.meta.env.VITE_DEV_USER as string | undefined;

  // Phase 24-02: declared above the useEffect that references it (was below;
  // function-declaration hoisting works at runtime but the react-hooks plugin
  // flags use-before-declared statically). useCallback gives a stable
  // reference so the effect doesn't re-run when the function identity changes.
  const handleCredentialResponse = useCallback(
    async (response: { credential: string }) => {
      setError(null);
      setLoading(true);
      try {
        const returnTo = new URLSearchParams(window.location.search).get('return_to') ?? undefined;
        const result = await api.auth.login(response.credential, returnTo);
        if (result.data.redirect_to) {
          window.location.assign(result.data.redirect_to);
          return;
        }
        // 14-05: role + capability-based default landing.
        //   basic_access + can_clock_in=true → Time PWA
        //   basic_access + can_clock_in=false → /no-access
        //   Higher roles → CB (Layout adds Clock-in side menu
        //                   for those with can_clock_in=true)
        const role = result.data.role;
        if (role === 'basic_access') {
          if (result.data.can_clock_in) {
            window.location.assign('/time/home');
          } else {
            navigate('/no-access', { replace: true });
          }
          return;
        }
        navigate('/', { replace: true });
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Login failed';
        if (msg.includes('403') || msg.includes('Access denied')) {
          setError('Access denied — your account does not have access to this application.');
        } else {
          setError(msg);
        }
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  useEffect(() => {
    if (isDev && devUser) {
      navigate('/', { replace: true });
      return;
    }

    if (!GOOGLE_CLIENT_ID) {
      // Phase 25-03: error already set via initial-state derivation above;
      // skip the GIS-init logic since `initialize({ client_id: undefined })`
      // would error.
      return;
    }

    const initGoogle = () => {
      if (!window.google || !buttonRef.current) return;

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
        auto_select: false,
        ux_mode: 'popup',
      });

      window.google.accounts.id.renderButton(buttonRef.current, {
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        width: 300,
      });
    };

    // GIS script may not be loaded yet — poll briefly
    if (window.google) {
      initGoogle();
    } else {
      const interval = setInterval(() => {
        if (window.google) {
          clearInterval(interval);
          initGoogle();
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [isDev, devUser, navigate, handleCredentialResponse]);

  return (
    <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-sm text-center">
        <img src={logoImage} alt="Reprotec" className="h-10 w-auto mx-auto mb-2" />
        <h1 className="text-[15px] text-neutral-600 mb-6">Work</h1>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-[13px] text-red-700">
            {error}
          </div>
        )}

        {loading && <p className="text-[13px] text-neutral-500 mb-4">Signing in...</p>}

        {/* Google Sign-In button renders here */}
        <div ref={buttonRef} className="flex justify-center" />

        <p className="mt-6 text-[11px] text-neutral-400">Sign in with your Google Account</p>
      </div>
    </div>
  );
}
